import React, { useMemo, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { adaptLegacyResult, downloadAsPDF, downloadAsDocx, copyForGoogleDocs } from '../../lib/episodeDocExport';
import { QUALITY_MODES } from '../../lib/episodeBrief';

// Renders the unified episode brief document (Hook + Interview Questions +
// Script/Talking Points) as a single scroll surface. Works for both the
// v2 `result.document` shape and legacy multi-section episodes (adapted
// on the fly by adaptLegacyResult).
//
// Two download buttons in the header: PDF + .docx. Both run client-side
// via jsPDF and the `docx` npm package.

// Highlight inline [VIRAL CLIP] / [GOLD NUGGET] tags the producer dropped
// into the script (mini-spec #57). The tag becomes a glowing badge before
// the line so the host can spot the moment at a glance.
function renderLineWithTags(line, key) {
  const tag = line.match(/^\s*\[(VIRAL CLIP|GOLD NUGGET)\]\s*(.*)$/i);
  if (tag) {
    const kind = tag[1].toUpperCase();
    const rest = tag[2];
    const cls  = kind === 'VIRAL CLIP' ? 'epi-doc-tag epi-doc-tag--viral' : 'epi-doc-tag epi-doc-tag--gold';
    const label = kind === 'VIRAL CLIP'
      ? '🎬 VIRAL CLIP — clip this for shorts'
      : '✨ GOLD NUGGET — pull-quote moment';
    return (
      <React.Fragment key={key}>
        <span className={cls}>{label}</span>
        {rest}
      </React.Fragment>
    );
  }
  return <React.Fragment key={key}>{line}</React.Fragment>;
}

function renderScript(md) {
  if (!md) return null;
  const blocks = md.split(/\n{2,}/);
  return blocks.map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    const headMatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (headMatch) {
      const level = headMatch[1].length;
      const text = headMatch[2];
      const Tag = level === 1 ? 'h3' : 'h4';
      return <Tag key={i} className="epi-doc-script-heading">{text}</Tag>;
    }
    const parts = trimmed.split('\n');
    return (
      <p key={i} className="epi-doc-script-p">
        {parts.map((p, j) => (
          <React.Fragment key={j}>
            {renderLineWithTags(p, `${i}-${j}`)}
            {j < parts.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

export default function EpisodeDocumentView({ episode, onBack }) {
  const toast = useToast();
  const [pdfBusy,  setPdfBusy]  = useState(false);
  const [docxBusy, setDocxBusy] = useState(false);

  // Adapt legacy results so the library still renders cleanly under the
  // new single-doc surface.
  const doc = useMemo(() => adaptLegacyResult(episode?.result) || null, [episode]);

  if (!doc) {
    return (
      <div className="epi-doc-empty">
        <p>This episode doesn\u2019t have a document yet.</p>
        {onBack && <button className="btn-ghost" onClick={onBack}>Back</button>}
      </div>
    );
  }

  const qualityMeta = QUALITY_MODES[doc.quality] || QUALITY_MODES.standard;
  const isInterview = doc.format === 'interview';
  const hasTalkingPoints = isInterview && doc.talking_points?.length > 0;
  const generatedOn = doc.generated_at
    ? new Date(doc.generated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  async function handlePdf() {
    setPdfBusy(true);
    try {
      await downloadAsPDF(doc);
      toast?.success?.('PDF downloaded.');
    } catch (err) {
      console.error('PDF export:', err);
      toast?.error?.('PDF export failed.');
    } finally { setPdfBusy(false); }
  }

  async function handleDocx() {
    setDocxBusy(true);
    try {
      await downloadAsDocx(doc);
      toast?.success?.('DOCX downloaded.');
    } catch (err) {
      console.error('DOCX export:', err);
      toast?.error?.('DOCX export failed.');
    } finally { setDocxBusy(false); }
  }

  function handleGoogleDocs() {
    // Open the blank doc synchronously (inside the click) so the popup
    // isn't blocked, then copy the formatted HTML for the user to paste.
    let win = null;
    try { win = window.open('https://docs.new', '_blank', 'noopener,noreferrer'); } catch {}
    copyForGoogleDocs(doc).then((ok) => {
      if (ok) toast?.success?.('Formatting copied — paste (⌘V / Ctrl+V) into the new Google Doc.');
      else toast?.error?.('Couldn’t copy — use the .docx download instead (it opens in Google Docs too).');
    });
  }

  return (
    <article className="epi-doc">
      {/* Header */}
      <header className="epi-doc-head">
        <div className="epi-doc-head-meta">
          <span className="epi-doc-head-eyebrow">Episode Brief</span>
          {generatedOn && <span className="epi-doc-head-date">{generatedOn}</span>}
          {!doc.legacy && (
            <span className={`epi-doc-head-quality epi-doc-head-quality--${doc.quality}`}>
              {qualityMeta.label}
            </span>
          )}
        </div>
        <h1 className="epi-doc-title">{doc.title}</h1>

        <div className="epi-doc-head-actions">
          <button
            type="button"
            className="btn-ghost epi-doc-download"
            onClick={handleGoogleDocs}
            title="Opens a new Google Doc and copies the formatted script to paste in"
          >
            <DownloadIcon />
            Google Docs
          </button>
          <button
            type="button"
            className="btn-ghost epi-doc-download"
            onClick={handleDocx}
            disabled={docxBusy}
          >
            {docxBusy ? 'Preparing\u2026' : (
              <>
                <DownloadIcon />
                .docx
              </>
            )}
          </button>
          <button
            type="button"
            className="btn-primary epi-doc-download"
            onClick={handlePdf}
            disabled={pdfBusy}
          >
            {pdfBusy ? 'Preparing\u2026' : (
              <>
                <DownloadIcon />
                PDF
              </>
            )}
          </button>
        </div>
      </header>

      {/* Marketing Hook */}
      {doc.hook && (
        <section className="epi-doc-section">
          <h2 className="epi-doc-section-head">Marketing Hook</h2>
          <p className="epi-doc-hook">{doc.hook}</p>
        </section>
      )}

      {/* Hook alternates (mini-spec #57) — 5–10 alternate hooks the
          producer drafted alongside the lead. Useful when the host
          wants a different shape (provocative, scene, data, etc.). */}
      {Array.isArray(doc.hook_alternates) && doc.hook_alternates.length > 0 && (
        <section className="epi-doc-section">
          <h2 className="epi-doc-section-head">More hooks to choose from</h2>
          <ol className="epi-doc-hook-alternates">
            {doc.hook_alternates.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ol>
        </section>
      )}

      {/* Title alternates */}
      {Array.isArray(doc.title_alternates) && doc.title_alternates.length > 0 && (
        <section className="epi-doc-section">
          <h2 className="epi-doc-section-head">Alternate titles</h2>
          <ul className="epi-doc-title-alternates">
            {doc.title_alternates.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Re-engagement moments (mini-spec #57) — drop these between
          Main Body beats at ~25/50/75% to fight drop-off. */}
      {Array.isArray(doc.reengagement_moments) && doc.reengagement_moments.length > 0 && (
        <section className="epi-doc-section">
          <h2 className="epi-doc-section-head">Re-engagement beats</h2>
          <p className="epi-doc-section-sub">Drop these between Main Body beats so listeners stay through the drop-off cliff.</p>
          <ul className="epi-doc-reengage-list">
            {doc.reengagement_moments.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Interview Questions */}
      {doc.questions?.length > 0 && (
        <section className="epi-doc-section">
          <h2 className="epi-doc-section-head">Interview Questions</h2>
          <ol className="epi-doc-questions">
            {doc.questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ol>
        </section>
      )}

      {/* Script or Talking Points */}
      {hasTalkingPoints ? (
        <section className="epi-doc-section">
          <h2 className="epi-doc-section-head">Talking Points</h2>
          <ul className="epi-doc-talking-points">
            {doc.talking_points.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </section>
      ) : doc.script ? (
        <section className="epi-doc-section">
          <h2 className="epi-doc-section-head">Script</h2>
          <div className="epi-doc-script">
            {renderScript(doc.script)}
          </div>
        </section>
      ) : null}

      <footer className="epi-doc-foot">
        <span>Built with Epio</span>
        {doc.model_used && <span className="epi-doc-foot-model">{doc.model_used}</span>}
      </footer>
    </article>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}
